import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import i18n from "../../i18n";
import { ActionType, IPagination, Order } from "../common/Interfaces";
import { getComparator } from "../common/utils";

export interface IClass {
  id: number;
  name: string;
  grade: string;
  gradeId: number;
  teacher: string;
  teacherId: number;
  students: number;
}

interface IClassEntities {
  [key: number]: IClass;
}
export enum ClassesFilterTypes {
  Class = "Class",
  Grade = "Grade",
  Teacher = "Teacher",
}
export enum ClassesColumns {
  Class = "Class",
  Grade = "Grade",
  Teacher = "Teacher",
  Students = "Students",
}

export enum ClassesActionLabels {
  AddStudent = "AddStudent",
  Edit = "Edit",
  Delete = "Delete",
}

interface IClassesState {
  entities: IClassEntities;
  pagination: IPagination;
  actionMode: ActionType;
  filterType: ClassesFilterTypes;
  searchText: string;
  order: Order;
  orderBy: ClassesColumns;
  updateItem: IClass | null;
  deleteItem: IClass | null;
}

const initialState: IClassesState = {
  entities: {},
  pagination: {
    currentPage: 1,
    totalPages: 1,
    pageSize: 15,
  },
  actionMode: ActionType.list,
  filterType: ClassesFilterTypes.Class,
  searchText: "",
  order: "asc",
  orderBy: ClassesColumns.Class,
  updateItem: null,
  deleteItem: null,
};
export const classesSlice = createSlice({
  name: "classes",
  initialState,
  reducers: {
    classesLoaded(state: IClassesState, action: PayloadAction<IClass[]>) {
      state.entities = action.payload.reduce<IClassEntities>(
        (entities, item) => {
          entities[item.id] = item;
          return entities;
        },
        {}
      );
      state.pagination.totalPages = Math.ceil(
        action.payload.length / state.pagination.pageSize
      );
    },
    currentPageSet(state: IClassesState, action: PayloadAction<number>) {
      state.pagination.currentPage = action.payload;
    },
    totalPagesSet(state: IClassesState, action: PayloadAction<number>) {
      state.pagination.totalPages = action.payload;
    },
    actionModeSet(state: IClassesState, action: PayloadAction<ActionType>) {
      state.actionMode = action.payload;
    },
    filterSet(state: IClassesState, action: PayloadAction<ClassesFilterTypes>) {
      state.filterType = action.payload;
    },
    searchTextSet(state: IClassesState, action: PayloadAction<string>) {
      state.searchText = action.payload;
    },
    orderSet(state: IClassesState, action: PayloadAction<Order>) {
      state.order = action.payload;
    },
    orderBySet(state: IClassesState, action: PayloadAction<ClassesColumns>) {
      state.orderBy = action.payload;
    },
    updateItemSet(state: IClassesState, action: PayloadAction<IClass | null>) {
      state.updateItem = action.payload;
    },
    deleteItemSet(state: IClassesState, action: PayloadAction<IClass | null>) {
      state.deleteItem = action.payload;
    },
  },
});

export const selectOrder = (state: RootState): Order => state.classes.order;
export const selectOrderBy = (state: RootState) => state.classes.orderBy;
export const selectUpdateItem = (state: RootState) => state.classes.updateItem;
export const selectDeleteItem = (state: RootState) => state.classes.deleteItem;
const selectClassEntities = (state: RootState) => state.classes.entities;
export const selectClasses = createSelector(
  selectClassEntities,
  (entities) => Object.values(entities) as IClass[]
);
export const selectClassesByGrade = createSelector(
  selectClasses,
  (classes: any, gradeId: any) => gradeId,
  (classes, gradeId) => classes.filter((item) => item.gradeId === gradeId)
);
export const selectFilterType = (state: RootState) => state.classes.filterType;
export const selectSearchText = (state: RootState) => state.classes.searchText;

export const selectFilteredClasses = createSelector(
  selectClasses,
  selectFilterType,
  selectSearchText,
  (classes, filter, text) => {
    const list = classes.filter((item) => {
      let searchIndex: string =
        ClassesFilterTypes[filter as keyof typeof ClassesFilterTypes];
      if (searchIndex === "Class") searchIndex = "name";
      let field = item[searchIndex.toLowerCase() as keyof IClass];
      field =
        searchIndex === "Grade" ? (i18n.t(field as string) as string) : field;
      const result = text
        ? field.toString().toLowerCase().includes(text.toLowerCase())
        : true;
      return result;
    });
    return list;
  }
);
export const selectClassesIDs = createSelector(selectClasses, (classes) =>
  classes.map((item) => item.id)
);
export const selectClassById = (state: RootState, classId: number) =>
  state.classes.entities[classId];

export const selectClassesPagination = (state: RootState): IPagination =>
  state.classes.pagination;

export const currentPageClassList = createSelector(
  selectFilteredClasses,
  selectOrder,
  selectOrderBy,
  selectClassesPagination,
  (list, order, orderBy, pagination: IPagination) => {
    const startIndex = (pagination.currentPage - 1) * pagination.pageSize;
    const endIndex = startIndex + pagination.pageSize;
    const sortedList = list
      .sort(
        getComparator<IClass>(
          order,
          (orderBy === "Class" ? "name" : orderBy.toLowerCase()) as keyof IClass
        )
      )
      .slice(startIndex, endIndex);
    return sortedList;
  }
);

export const selectClassesActionMode = (state: RootState): ActionType =>
  state.classes.actionMode;

export const {
  classesLoaded,
  currentPageSet,
  totalPagesSet,
  actionModeSet,
  filterSet,
  searchTextSet,
  orderBySet,
  orderSet,
  updateItemSet,
  deleteItemSet,
} = classesSlice.actions;

export default classesSlice.reducer;
